import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useLocations } from '@/features/inventory/hooks';
import {
  PO_RECEIVABLE_STATUSES,
  PO_STATUS_LABELS,
  PO_STATUS_VARIANTS,
  usePurchaseOrders,
  useReceivePurchaseOrder,
  type PurchaseOrder,
} from '@/features/procurement/hooks';
import {
  centsToInputDollars,
  formatCurrency,
  formatDateOnly,
  inputDollarsToCents,
  todayIsoDate,
} from '@/lib/formatters';
import { formatQtyUom, formatUnitCostCents, isPackUnit, toStockQty } from '@/lib/uom';

interface ReceiveLineDraft {
  poLineId: string;
  sku: string;
  name: string;
  /** Unit the PO line is ordered and received in. */
  uom: string;
  /** Item's stock unit, what on-hand counts in. */
  baseUom: string;
  /** Stock units per `uom`. */
  conversion: number;
  /** One-off PO line. Arrival is recorded, but nothing is added to on-hand. */
  custom: boolean;
  isLotTracked: boolean;
  ordered: number;
  alreadyReceived: number;
  quantity: string;
  unitCost: string;
  locationId: string;
  lotNumber: string;
}

interface ReceiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect a PO (e.g. launched from the PO table's Receive action). */
  purchaseOrderId?: string | null;
}

/**
 * Receiving form: pick an open PO, confirm what arrived per line (qty,
 * actual unit cost, destination location, lot), and post it. Posting adds
 * the quantities to on-hand, records the cost against each catalog item,
 * and moves the PO to Partially received or Delivered. A custom line is
 * marked received so the order can close, and is not added to inventory.
 */
export function ReceiveDialog({ open, onOpenChange, purchaseOrderId }: ReceiveDialogProps) {
  const receive = useReceivePurchaseOrder();
  const { data: purchaseOrders } = usePurchaseOrders();
  const { data: locations } = useLocations();

  const [poId, setPoId] = useState('');
  const [receivedAt, setReceivedAt] = useState(todayIsoDate());
  const [packingSlip, setPackingSlip] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReceiveLineDraft[]>([]);

  const receivable = useMemo(
    () => (purchaseOrders ?? []).filter((po) => PO_RECEIVABLE_STATUSES.includes(po.status)),
    [purchaseOrders],
  );
  const po: PurchaseOrder | null = useMemo(
    () => (purchaseOrders ?? []).find((p) => p.id === poId) ?? null,
    [purchaseOrders, poId],
  );

  useEffect(() => {
    if (!open) return;
    setPoId(purchaseOrderId ?? '');
    setReceivedAt(todayIsoDate());
    setPackingSlip('');
    setNotes('');
  }, [open, purchaseOrderId]);

  // Rebuild the line drafts only when the selected PO changes (not on every
  // refetch, which would wipe edits): default each line to its outstanding
  // quantity at the PO's unit price and ship-to.
  const builtFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      builtFor.current = null;
      return;
    }
    if (!po) {
      builtFor.current = null;
      setLines([]);
      return;
    }
    if (builtFor.current === po.id && lines.length > 0) return;
    builtFor.current = po.id;
    const defaultLocation =
      po.ship_to_location_id ??
      (locations ?? []).find((l) => l.kind === 'warehouse')?.id ??
      locations?.[0]?.id ??
      '';
    setLines(
      po.po_lines.map((l) => {
        const outstanding = Math.max(0, l.quantity - l.received_quantity);
        return {
          poLineId: l.id,
          sku: l.items?.sku ?? '',
          name: l.items?.name ?? l.description ?? 'Custom item',
          uom: l.uom || l.items?.base_uom || 'each',
          baseUom: l.items?.base_uom ?? l.uom,
          conversion: Number(l.conversion_to_base) || 1,
          custom: !l.item_id,
          isLotTracked: l.items?.is_lot_tracked ?? false,
          ordered: l.quantity,
          alreadyReceived: l.received_quantity,
          quantity: outstanding > 0 ? String(outstanding) : '',
          unitCost: centsToInputDollars(Math.round(l.unit_price_cents)),
          locationId: defaultLocation,
          lotNumber: '',
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `lines` is read only to skip rebuilds
  }, [open, po, locations]);

  const updateLine = (poLineId: string, patch: Partial<ReceiveLineDraft>) =>
    setLines((prev) => prev.map((l) => (l.poLineId === poLineId ? { ...l, ...patch } : l)));

  const receivingCount = lines.filter((l) => Number(l.quantity) > 0).length;
  const fullyReceivedAfter =
    lines.length > 0 &&
    lines.every((l) => l.alreadyReceived + (Number(l.quantity) || 0) >= l.ordered);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!po) {
      toast.error('Pick a purchase order.');
      return;
    }
    const payload = [];
    try {
      for (const l of lines) {
        if (l.quantity.trim() === '') continue;
        const qty = Number(l.quantity);
        const label = l.sku || l.name;
        if (!Number.isInteger(qty) || qty < 0)
          throw new Error(`${label}: quantity must be a whole number.`);
        if (qty === 0) continue;
        if (!l.locationId) throw new Error(`${label}: pick a location to receive into.`);
        payload.push({
          po_line_id: l.poLineId,
          quantity: qty,
          to_location_id: l.locationId,
          unit_cost_cents: inputDollarsToCents(l.unitCost),
          lot_number: l.isLotTracked ? l.lotNumber || null : null,
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check the receiving lines.');
      return;
    }
    if (payload.length === 0) {
      toast.error('Enter a received quantity on at least one line.');
      return;
    }

    try {
      await receive.mutateAsync({
        purchaseOrderId: po.id,
        // Noon local keeps the calendar date stable across time zones.
        receivedAt: new Date(`${receivedAt}T12:00:00`).toISOString(),
        packingSlipNumber: packingSlip || null,
        notes: notes || null,
        lines: payload,
      });
      toast.success(
        fullyReceivedAfter
          ? `${po.po_number} fully received and marked delivered`
          : `Received ${payload.length} line${payload.length === 1 ? '' : 's'} on ${po.po_number}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not post the receipt.');
    }
  };

  const submitting = receive.isPending;
  const locationRows = locations ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Receive items</DialogTitle>
          </DialogHeader>

          <div className="mt-5 space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5 lg:col-span-2">
                <Label htmlFor="rcv-po">
                  Purchase order <span className="text-fresh-salmon-dark">*</span>
                </Label>
                <Select value={poId} onValueChange={setPoId}>
                  <SelectTrigger id="rcv-po">
                    <SelectValue
                      placeholder={receivable.length ? 'Pick an open PO' : 'No open POs'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {receivable.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.po_number} — {p.vendors?.name ?? 'Unknown vendor'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rcv-date">Received on</Label>
                <Input
                  id="rcv-date"
                  type="date"
                  value={receivedAt}
                  max={todayIsoDate()}
                  onChange={(e) => setReceivedAt(e.target.value || todayIsoDate())}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rcv-slip">Packing slip #</Label>
                <Input
                  id="rcv-slip"
                  value={packingSlip}
                  onChange={(e) => setPackingSlip(e.target.value)}
                  placeholder="Optional"
                  maxLength={120}
                  className="font-mono text-xs"
                />
              </div>
            </section>

            {po && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-border bg-secondary/30 px-4 py-3 text-sm">
                <Badge variant={PO_STATUS_VARIANTS[po.status]}>{PO_STATUS_LABELS[po.status]}</Badge>
                <span>
                  <span className="text-muted-foreground">Expected </span>
                  {formatDateOnly(po.expected_at)}
                </span>
                {po.tracking_number && (
                  <span>
                    <span className="text-muted-foreground">{po.carrier ?? 'Tracking'} </span>
                    <span className="font-mono text-xs">{po.tracking_number}</span>
                  </span>
                )}
                <span>
                  <span className="text-muted-foreground">PO total </span>
                  <span className="tabular-nums">{formatCurrency(po.total_cents)}</span>
                </span>
              </div>
            )}

            {po && (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Item</th>
                      <th className="w-28 px-2 py-2 text-right font-medium">Ordered / recv'd</th>
                      <th className="w-24 px-2 py-2 text-right font-medium">Receiving</th>
                      <th className="w-28 px-2 py-2 text-right font-medium">Unit cost</th>
                      <th className="w-48 px-2 py-2 text-left font-medium">Into location</th>
                      <th className="w-36 px-2 py-2 text-left font-medium">Lot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const qty = Number(l.quantity) || 0;
                      const over = l.alreadyReceived + qty > l.ordered;
                      const packed = !l.custom && isPackUnit(l.conversion);
                      return (
                        <tr key={l.poLineId} className="border-t border-border align-top">
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs text-muted-foreground">
                              {l.custom ? 'Custom' : l.sku}
                            </div>
                            <div className="truncate">{l.name}</div>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                            {l.ordered.toLocaleString()} / {l.alreadyReceived.toLocaleString()}
                            <div className="text-[11px]">{l.uom}</div>
                            {packed && (
                              <div className="text-[11px]">1 {l.uom} = {formatQtyUom(l.conversion, l.baseUom)}</div>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <Input
                              value={l.quantity}
                              onChange={(e) =>
                                updateLine(l.poLineId, {
                                  quantity: e.target.value.replace(/[^\d]/g, ''),
                                })
                              }
                              inputMode="numeric"
                              placeholder="0"
                              aria-label={`Quantity received for ${l.sku || l.name}`}
                              className="h-8 text-right tabular-nums"
                            />
                            {packed && qty > 0 && (
                              <div className="mt-0.5 text-right text-[11px] tabular-nums text-muted-foreground">
                                +{formatQtyUom(toStockQty(qty, l.conversion), l.baseUom)} to stock
                              </div>
                            )}
                            {over && (
                              <div className="mt-0.5 text-right text-[11px] text-fresh-salmon-dark">
                                Over PO qty
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <Input
                              value={l.unitCost}
                              onChange={(e) => updateLine(l.poLineId, { unitCost: e.target.value })}
                              inputMode="decimal"
                              placeholder="0.00"
                              aria-label={`Unit cost for ${l.sku || l.name}`}
                              className="h-8 text-right tabular-nums"
                            />
                            <div className="mt-0.5 text-right text-[11px] tabular-nums text-muted-foreground">
                              per {l.uom}
                              {packed && l.unitCost.trim() && (
                                <span className="block">
                                  {(() => {
                                    try {
                                      const cents = inputDollarsToCents(l.unitCost);
                                      return cents == null
                                        ? null
                                        : `${formatUnitCostCents(cents / l.conversion)}/${l.baseUom}`;
                                    } catch {
                                      return null;
                                    }
                                  })()}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <Select
                              value={l.locationId}
                              onValueChange={(v) => updateLine(l.poLineId, { locationId: v })}
                            >
                              <SelectTrigger
                                className="h-8"
                                aria-label={`Location for ${l.sku || l.name}`}
                              >
                                <SelectValue placeholder="Pick a location" />
                              </SelectTrigger>
                              <SelectContent>
                                {locationRows.map((loc) => (
                                  <SelectItem key={loc.id} value={loc.id}>
                                    {loc.name} ({loc.code})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-2">
                            {l.isLotTracked ? (
                              <Input
                                value={l.lotNumber}
                                onChange={(e) =>
                                  updateLine(l.poLineId, { lotNumber: e.target.value })
                                }
                                placeholder="Lot #"
                                maxLength={120}
                                aria-label={`Lot number for ${l.sku}`}
                                className="h-8 font-mono text-xs"
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {locationRows.length === 0 && (
              <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                No active locations yet.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="rcv-notes">Notes</Label>
              <Textarea
                id="rcv-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Damage, shortages, substitutions…"
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6 sm:items-center">
            {po && receivingCount > 0 && (
              <span className="mr-auto text-xs text-muted-foreground">
                {fullyReceivedAfter
                  ? 'This completes the PO — it will be marked Delivered.'
                  : 'The PO will be marked Partially received.'}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !po || receivingCount === 0 || locationRows.length === 0}
            >
              {submitting ? 'Posting…' : 'Post receipt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
